import React, { useContext } from 'react';
import { DrawerContext } from './useDrawer';
import { useNavigate } from 'react-router-dom';
import usePopUp from '@hooks/usePopUp';
import { useMsal } from '@azure/msal-react';
import { appConfig } from '@configs/index';
import { Avatar, Box, Collapse, Drawer, List, Stack, Tooltip } from '@mui/material';
import color from '@theme/Colors';
import { DrawerHeader } from './MyDrawer';
import LanguageSelect from '@components/LanguageSelect';
import { FiArrowLeft } from 'react-icons/fi';
import ItemDrawer from './DrawerItem';
import LogoutIcon from '@assets/drawer-icon/LogoutIcon';
import i18n from 'src/translations/config';
import PopUpWarning from '@components/PopUpWarning';
import { ExpandLess, ExpandMore } from '@mui/icons-material';

export default function DrawerSmall() {
    const { instance, accounts } = useMsal();
    const { t } = i18n;
    const {
        openDrawerSmall,
        setOpenDrawerSmall,
        ListDrawItem,
        activeItem,
        setActiveItem,
        isMobileSM,
        isMediumMD,
        setIsShowDrawer,
        openGroups,
        toggleGroup,
    } = useContext(DrawerContext);
    const navigate = useNavigate();

    const toggleDrawer = (open: boolean) => (event: React.KeyboardEvent | React.MouseEvent) => {
        if (
            event.type === 'keydown' &&
            ((event as React.KeyboardEvent).key === 'Tab' || (event as React.KeyboardEvent).key === 'Shift')
        ) {
            return;
        }
        if (isMobileSM) {
            setIsShowDrawer(false);
        } else if (isMediumMD) {
            setIsShowDrawer(true);
        }
        setOpenDrawerSmall(open);
    };

    const popUpWarning = usePopUp({
        onConfirm() {
            const currentAccount = instance.getAccountByHomeId(accounts[0].homeAccountId);
            console.log(currentAccount?.homeAccountId);
            instance.logoutRedirect({ account: currentAccount, postLogoutRedirectUri: '/' });
        },
    });
    const isShowMultiLanguage = appConfig.flagFeatures.multiLanguage;

    return (
        <React.Fragment key={1}>
            <PopUpWarning {...popUpWarning} message="Are you sure you want to log out?" />

            <Drawer
                anchor={'left'}
                open={openDrawerSmall}
                onClose={toggleDrawer(false)}
                sx={{
                    pt: 5,
                    '& .MuiPaper-root': {
                        background: color.grey100,
                        pt: 8,
                        px: 1,
                        overflow: 'visible',
                    },
                }}
            >
                <Box sx={{ width: 220, display: 'flex', flexDirection: 'column', height: '100vh' }} role="presentation">
                    <DrawerHeader sx={{ padding: 0, justifyContent: 'center', flexShrink: 0 }} className="drawer-header">
                        <Stack direction={'row'}>
                            {isShowMultiLanguage && (
                                <Stack sx={{ display: 'block' }}>
                                    <LanguageSelect isShowLabel={true} />
                                </Stack>
                            )}
                            <Avatar
                                sx={[{
                                    background: color.grey100,
                                    color: color.grey600,
                                    display: 'flex',
                                    width: '30px',
                                    height: '30px',
                                    position: 'absolute',
                                    boxShadow: '0 0 3px 2px rgba(0, 0, 0, 0.2) !important',
                                    zIndex: 9,
                                    right: '-15px',
                                }]}
                                onClick={toggleDrawer(false)}
                            >
                                <FiArrowLeft style={{ opacity: 1, cursor: 'pointer' }} />
                            </Avatar>
                        </Stack>
                    </DrawerHeader>

                    <Box sx={{
                        flex: 1, overflowY: 'auto', overflowX: 'hidden', minHeight: 0,
                        scrollbarWidth: 'thin',
                        scrollbarColor: 'rgba(0,0,0,0.15) transparent',
                        '&::-webkit-scrollbar': { width: 4 },
                        '&::-webkit-scrollbar-track': { background: 'transparent' },
                        '&::-webkit-scrollbar-thumb': { background: 'rgba(0,0,0,0.15)', borderRadius: 2 },
                        '&::-webkit-scrollbar-thumb:hover': { background: 'rgba(0,0,0,0.25)' },
                    }}>
                        <List disablePadding>
                            {ListDrawItem.map((item, index) => {
                                if (item.children) {
                                    const isGroupActive = item.children.some(c => c.id === activeItem);
                                    return (
                                        <React.Fragment key={index}>
                                            <ItemDrawer
                                                icon={item.icon}
                                                isActive={isGroupActive}
                                                label={item.label}
                                                open={openDrawerSmall}
                                                isSmallDrawer={true}
                                                endIcon={openGroups[item.id] ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                                                onClick={() => toggleGroup(item.id)}
                                            />
                                            <Collapse in={!!openGroups[item.id]} timeout="auto" unmountOnExit>
                                                <List disablePadding>
                                                    {item.children.map((child, ci) => (
                                                        <ItemDrawer
                                                            key={ci}
                                                            icon={child.icon}
                                                            isActive={child.id === activeItem}
                                                            label={child.label}
                                                            open={openDrawerSmall}
                                                            isSmallDrawer={true}
                                                            isSubItem
                                                            onClick={() => {
                                                                setActiveItem(child.id);
                                                                navigate(child.path);
                                                                setOpenDrawerSmall(false);
                                                                setIsShowDrawer(false);
                                                            }}
                                                        />
                                                    ))}
                                                </List>
                                            </Collapse>
                                        </React.Fragment>
                                    );
                                }

                                return (
                                    <Tooltip key={index} title={item.label} placement="right">
                                        <React.Fragment>
                                            <ItemDrawer
                                                icon={item.icon}
                                                isActive={item.id === activeItem}
                                                label={item.label}
                                                open={openDrawerSmall}
                                                isSmallDrawer={true}
                                                onClick={() => {
                                                    setActiveItem(item.id);
                                                    navigate(item.path);
                                                    setOpenDrawerSmall(false);
                                                    setIsShowDrawer(false);
                                                }}
                                            />
                                        </React.Fragment>
                                    </Tooltip>
                                );
                            })}
                        </List>
                    </Box>

                    <Box sx={{ mt: 'auto', px: 0, pb: 1, flexShrink: 0 }}>
                        <ItemDrawer
                            key={99}
                            icon={<LogoutIcon className="svg-item-Menu" />}
                            isActive={false}
                            label={t('drawer.logout')}
                            open={openDrawerSmall}
                            isSmallDrawer={true}
                            onClick={popUpWarning.setTrue}
                        />
                    </Box>
                </Box>
            </Drawer>
        </React.Fragment>
    );
}
